import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { Separator } from "../ui/separator";
import { DownIcon, PlayedIcon, WishlistIcon } from "./icons";

const BUTTON_ACTIONS = {
  COMPLETED: "COMPLETED",
  SAVED: "SAVED",
} as const;
type BUTTON_ACTIONS = (typeof BUTTON_ACTIONS)[keyof typeof BUTTON_ACTIONS];

interface ButtonWithMenuProps {
  className?: string;
  isCompleted: boolean;
  isSaved: boolean;
  onCompleted(): void;
  onSaved(): void;
}

export default function ButtonWithMenu({
  className,
  isCompleted,
  isSaved,
  onCompleted,
  onSaved,
}: ButtonWithMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuItem = (action: BUTTON_ACTIONS) => () => {
    setIsOpen(false);
    if (action === BUTTON_ACTIONS.COMPLETED) {
      onCompleted();
    }
    if (action === BUTTON_ACTIONS.SAVED) {
      onSaved();
    }
  };

  const buttons: { action: BUTTON_ACTIONS; label: string }[] = [];

  if (isCompleted) {
    buttons.push({
      action: BUTTON_ACTIONS.COMPLETED,
      label: "Mark not done",
    });
  } else if (isSaved) {
    buttons.push({
      action: BUTTON_ACTIONS.COMPLETED,
      label: "Mark as done",
    });
    buttons.push({
      action: BUTTON_ACTIONS.SAVED,
      label: "Remove from wishlist",
    });
  } else {
    buttons.push({
      action: BUTTON_ACTIONS.COMPLETED,
      label: "Mark as done",
    });
    buttons.push({
      action: BUTTON_ACTIONS.SAVED,
      label: "Add to wishlist",
    });
  }

  const isModified = isCompleted || isSaved;

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      {isModified ? (
        <PopoverTrigger
          className={cn(
            "group h-[45px] rounded-[23px] border border-white/50 px-[17px] font-medium text-16 text-white",
            className
          )}
        >
          <span className="flex h-full items-center gap-2">
            {isCompleted ? <PlayedIcon /> : <WishlistIcon />}
            {isCompleted ? "Done" : "Wishlisted"}
            <DownIcon
              className={cn(
                "transition-transform group-data-[state=open]:rotate-180"
              )}
            />
          </span>
        </PopoverTrigger>
      ) : (
        <div
          className={cn(
            "flex! h-[45px] rounded-[23px] bg-linear-227 from-mint-turquoise to-72% to-bright-turquoise shadow-seafoam-green/80 transition-shadow hover:shadow-0-0-10",
            className
          )}
        >
          <Button
            className="h-full px-3.5 font-medium text-16 text-white md:px-[17px]"
            onClick={onCompleted}
          >
            Mark as done
          </Button>
          <Separator className="bg-tiffany-blue" orientation="vertical" />
          <PopoverTrigger
            aria-label="More"
            className="group h-full w-11 md:w-[49px]"
          >
            <DownIcon
              className={cn(
                "mx-auto transition-transform group-data-[state=open]:rotate-180"
              )}
            />
          </PopoverTrigger>
        </div>
      )}
      <PopoverContent
        align="start"
        asChild
        className="relative z-10 flex w-auto min-w-56 flex-col gap-[7px] rounded-[15px] border-0 bg-twilight-indigo p-[7px] shadow-0-0-4 shadow-deep-koamaru"
      >
        <ul>
          {buttons.map(({ action, label }) => (
            <li key={action}>
              <Button
                className={cn(
                  "h-8.5 w-full justify-start rounded-[10px] px-2.5 py-[5px] font-medium text-16 text-white transition-colors hover:*:text-white",
                  action === BUTTON_ACTIONS.COMPLETED
                    ? "hover:bg-teal"
                    : "hover:bg-berry"
                )}
                onClick={handleMenuItem(action)}
              >
                {action === BUTTON_ACTIONS.COMPLETED ? (
                  <PlayedIcon />
                ) : (
                  <WishlistIcon />
                )}
                {label}
              </Button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
